import React, { useState, useEffect, useRef } from "react";
import { getUniverseEvents, interpretUniverseEvent, reportUniverseEventError } from "../../../services/universeService";
import { getCurrentPackageUserModule } from "../../../services/api";
import ModuleHeader from "../../moduleHeader";
import { toast } from "react-toastify";
import { FiStar, FiMoon, FiRefreshCw } from "react-icons/fi";

const TABS = {
    SKY_EVENTS: "skyEvents",
    LUNAR_CALENDAR: "moonCalendars",
    RETROGRADES: "retroPeriods",
};

const MONTHS = [
    "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
    "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"
];

const SkyEvents = ({ id, moduleId, isOwner }) => {
    const [activeTab, setActiveTab] = useState(TABS.SKY_EVENTS);
    const [selectedMonth, setSelectedMonth] = useState(MONTHS[new Date().getMonth()]);
    const [year] = useState(new Date().getFullYear());

    const [packageData, setPackageData] = useState(null);
    const [initLoading, setInitLoading] = useState(true);
    const [isHeaderExpanded, setIsHeaderExpanded] = useState(false);

    const [data, setData] = useState({
        skyEvents: [],
        retroPeriods: [],
        moonCalendars: [],
    });
    const [loadingEvents, setLoadingEvents] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [interpreting, setInterpreting] = useState(false);
    const [reportingError, setReportingError] = useState(false);
    const [hasReported, setHasReported] = useState(false);

    // Mouse drag scrolling state
    const scrollRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);

    // Initial package load
    useEffect(() => {
        const fetchPackageData = async () => {
            try {
                if (id) {
                    const pkgData = await getCurrentPackageUserModule(id, moduleId || 28);
                    if (pkgData) setPackageData(pkgData);
                }
            } catch (error) {
                console.error("Paket verisi alınamadı:", error);
            } finally {
                setInitLoading(false);
            }
        };
        fetchPackageData();
    }, [id, moduleId]);

    // Fetch events on month change
    useEffect(() => {
        fetchData();
    }, [selectedMonth, year]);

    const fetchData = async () => {
        setLoadingEvents(true);
        try {
            const response = await getUniverseEvents(year, selectedMonth);
            if (response) {
                setData({
                    skyEvents: response.skyEvents || [],
                    retroPeriods: response.retroPeriods || [],
                    moonCalendars: response.moonCalendars || [],
                });
            }
        } catch (error) {
            console.error("Universe Guide Fetch error", error);
        } finally {
            setLoadingEvents(false);
        }
    };

    const handleMonthChange = (m) => {
        setSelectedMonth(m);
    };

    const onMouseDown = (e) => {
        setIsDragging(true);
        setStartX(e.pageX - scrollRef.current.offsetLeft);
        setScrollLeft(scrollRef.current.scrollLeft);
    };

    const onMouseLeave = () => {
        setIsDragging(false);
    };

    const onMouseUp = () => {
        setIsDragging(false);
    };

    const onMouseMove = (e) => {
        if (!isDragging) return;
        e.preventDefault();
        const x = e.pageX - scrollRef.current.offsetLeft;
        const walk = (x - startX) * 2; // Scroll speed multiplier
        scrollRef.current.scrollLeft = scrollLeft - walk;
    };

    const openModal = (eventData, type) => {
        setSelectedEvent({ ...eventData, type });
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedEvent(null);
        setHasReported(false);
    };

    const handleReportError = async () => {
        if (!selectedEvent || hasReported || reportingError) return;
        setReportingError(true);
        try {
            const backendTypeMap = {
                skyEvents: "sky_events",
                retroPeriods: "retro_periods",
                moonCalendars: "moon_calendars"
            };
            const response = await reportUniverseEventError(
                backendTypeMap[selectedEvent.type],
                selectedEvent.id,
                "Kullanıcı UI üzerinden hata bildirdi.",
                "Eksik veya anlamsız veri."
            );
            if (response.success) {
                toast.success(response.message || "Hata bildirildi.");
                setHasReported(true);
            }
        } catch (error) {
            toast.error("Hata bildirilemedi. Lütfen tekrar deneyin.");
        } finally {
            setReportingError(false);
        }
    };

    const handleUnlock = async () => {
        if (!selectedEvent) return;
        setInterpreting(true);
        try {
            const backendTypeMap = {
                skyEvents: "sky_events",
                retroPeriods: "retro_periods",
                moonCalendars: "moon_calendars"
            };

            const response = await interpretUniverseEvent(
                backendTypeMap[selectedEvent.type],
                selectedEvent.id
            );

            if (response.success) {
                toast.success("Öngörü başarıyla kilit açıldı!");
                setSelectedEvent(prev => ({ ...prev, is_unlocked: true, interpretation: response.interpretation }));

                setData(prev => {
                    const newData = { ...prev };
                    const idx = newData[selectedEvent.type].findIndex(e => e.id === selectedEvent.id);
                    if (idx !== -1) {
                        newData[selectedEvent.type][idx].is_unlocked = true;
                        newData[selectedEvent.type][idx].interpretation = response.interpretation;
                    }
                    return newData;
                });
            }
        } catch (error) {
            toast.error(error.response?.data?.message || "Token harcama başarısız oldu.");
            console.error(error);
        } finally {
            setInterpreting(false);
        }
    };

    const renderActiveTabContent = () => {
        if (loadingEvents) {
            return (
                <div className="flex justify-center items-center py-10">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3DA7B5]"></div>
                </div>
            );
        }

        const currentArray = data[activeTab] || [];

        if (currentArray.length === 0) {
            return (
                <div className="text-center py-10 text-gray-400 text-sm font-medium">
                    Bu ay için veri bulunamadı.
                </div>
            );
        }

        return (
            <div className="flex flex-col gap-4 pb-24">
                {currentArray.map((item, idx) => (
                    <div
                        key={idx}
                        className="bg-white border text-left border-gray-100 shadow-sm hover:shadow-md rounded-[1.25rem] p-5 flex flex-col gap-3 cursor-pointer transition-all hover:-translate-y-1 group relative overflow-hidden"
                        onClick={() => openModal(item, activeTab)}
                    >
                        <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-[#3DA7B5]/5 to-transparent rounded-bl-full pointer-events-none transition-all duration-300 group-hover:scale-110"></div>
                        <div className="flex justify-between items-start w-full">
                            <div className="text-[10px] font-black text-[#3DA7B5] uppercase tracking-[0.2em] bg-[#3DA7B5]/5 inline-flex items-center px-3 py-1.5 rounded-full">
                                {item.date_str}
                            </div>
                            {item.is_unlocked && (
                                <div className="inline-flex items-center px-2 py-1 rounded-full text-[9px] font-black bg-gradient-to-r from-emerald-100 to-emerald-50 text-emerald-600 tracking-wider">
                                    <svg className="w-2.5 h-2.5 mr-1" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path></svg>
                                    AÇIK
                                </div>
                            )}
                        </div>
                        <div className="flex flex-col gap-1.5 text-left relative z-10">
                            <h4 className="text-[15px] font-black text-gray-800 leading-snug tracking-tight group-hover:text-[#3DA7B5] transition-colors">{item.event}</h4>
                            <span className="text-[11px] text-gray-400 font-bold tracking-wide flex items-center gap-1.5">
                                <span className="w-1.5 h-1.5 rounded-full bg-gray-200"></span>
                                {item.degree}
                            </span>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    if (initLoading) {
        return (
            <div className="w-full h-screen min-h-screen flex items-center justify-center bg-white">
                <div className="flex flex-col items-center justify-center">
                    <div className="relative mb-8">
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="animate-spin rounded-full h-24 w-24 border-4 border-transparent border-t-[#3DA7B5] border-r-[#3DA7B5] opacity-75"></div>
                        </div>
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="animate-spin rounded-full h-16 w-16 border-[3px] border-transparent border-b-[#2a8a96] border-l-[#2a8a96] opacity-60" style={{ animationDirection: "reverse", animationDuration: "1.5s" }}></div>
                        </div>
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#3DA7B5] via-[#2a8a96] to-[#3DA7B5] animate-pulse shadow-2xl"></div>
                        </div>
                    </div>
                    <p className="text-xl font-semibold text-gray-800 animate-pulse">
                        Yükleniyor...
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full h-screen min-h-screen flex items-stretch justify-stretch sm:items-center sm:justify-center bg-[#e8e9ed] relative">
            <div className="flex flex-col items-center w-full min-h-full bg-white shadow-xl border border-gray-200 rounded-none sm:max-w-[390px] sm:h-[700px] sm:rounded-3xl relative overflow-y-auto">

                {/* Header Section */}
                {packageData && (
                    <div className="relative z-30 w-full mb-2">
                        <ModuleHeader
                            userName={
                                packageData.user?.first_name + " " + packageData.user?.last_name
                            }
                            subtitle={packageData.package?.name + " Modülüne Hoş Geldiniz"}
                            title={packageData.package?.name}
                            description={packageData.package?.description}
                            backgroundImage={packageData.package?.image_url}
                            onExpandedChange={setIsHeaderExpanded}
                            showToggle={true}
                            autoHeight={true}
                        />
                    </div>
                )}

                {/* Main Content Area */}
                <div className={`relative flex flex-col min-h-screen w-full text-gray-800 bg-white rounded-t-3xl z-20 shadow-[0_-8px_30px_-15px_rgba(0,0,0,0.1)] px-5 pt-6 pb-12 transition-all duration-300 ${isHeaderExpanded ? "-mt-4" : "-mt-8"}`}>


                    <div className="mt-4 bg-gray-100/50 p-1.5 rounded-2xl flex items-center justify-between mb-5 shadow-inner backdrop-blur-sm border border-gray-200/50">
                        {[
                            { id: TABS.SKY_EVENTS, label: "Gökyüzü", icon: <FiStar className="w-4 h-4" /> },
                            { id: TABS.LUNAR_CALENDAR, label: "Ay Takvimi", icon: <FiMoon className="w-4 h-4" /> },
                            { id: TABS.RETROGRADES, label: "Retrolar", icon: <FiRefreshCw className="w-4 h-4" /> }
                        ].map(tab => {
                            const isActive = activeTab === tab.id;
                            return (
                                <button
                                    key={tab.id}
                                    className={`flex-1 py-2.5 px-1 text-[12px] font-black rounded-[14px] transition-all duration-300 flex items-center justify-center gap-1.5 ${isActive
                                        ? "bg-white text-[#3DA7B5] shadow-sm border border-gray-50 transform scale-[1.02]"
                                        : "text-gray-400 hover:text-gray-600 hover:bg-white/40"
                                        }`}
                                    onClick={() => setActiveTab(tab.id)}
                                >
                                    <span className="flex items-center justify-center opacity-80">{tab.icon}</span>
                                    {tab.label}
                                </button>
                            );
                        })}
                    </div>

                    {/* MONTH SLIDER - Only show when Sky Events is selected */}
                    {activeTab === TABS.SKY_EVENTS && (
                        <div
                            ref={scrollRef}
                            onMouseDown={onMouseDown}
                            onMouseLeave={onMouseLeave}
                            onMouseUp={onMouseUp}
                            onMouseMove={onMouseMove}
                            className={`relative z-30 flex-shrink-0 w-full overflow-x-auto no-scrollbar mb-8 pb-4 -mx-1 px-1 ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
                            style={{ scrollbarWidth: "none", WebkitOverflowScrolling: "touch" }}
                        >
                            <div className="flex gap-2.5 min-w-max px-1">
                                {MONTHS.map(m => {
                                    const isSelected = selectedMonth === m;
                                    return (
                                        <button
                                            key={m}
                                            onClick={() => handleMonthChange(m)}
                                            className={`px-5 py-2.5 text-xs font-black rounded-2xl transition-all duration-300 whitespace-nowrap border ${isSelected
                                                ? "bg-gradient-to-r from-[#3DA7B5] to-[#4DBAC8] text-white shadow-lg shadow-[#3DA7B5]/20 border-transparent transform scale-105"
                                                : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-700"
                                                }`}
                                        >
                                            {m} {year}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* CONTENT LIST */}
                    <div className="flex-1 w-full relative">
                        {renderActiveTabContent()}
                    </div>
                </div>
            </div>

            {/* MODAL */}
            {showModal && selectedEvent && (
                <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4">
                    <div className="absolute inset-0 bg-gray-900/40 backdrop-blur-sm transition-opacity" onClick={closeModal}></div>

                    <div className="relative bg-white w-full sm:w-[390px] rounded-t-3xl sm:rounded-3xl max-h-[85vh] flex flex-col shadow-2xl animate-slideUp sm:animate-fadeIn">
                        <div className="w-12 h-1.5 bg-gray-200 rounded-full mx-auto mt-4 sm:hidden"></div>

                        <button
                            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 text-gray-500 rounded-full transition-colors z-10"
                            onClick={closeModal}
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                        </button>

                        <div className="p-6 overflow-y-auto">
                            <p className="text-[#3DA7B5] font-bold text-xs uppercase tracking-widest mb-2">{selectedEvent.date_str}</p>
                            <h3 className="text-xl font-black text-gray-800 leading-tight mb-6 pr-6">{selectedEvent.event}</h3>

                            {selectedEvent.is_unlocked ? (
                                <div className="space-y-4">
                                    <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-100 rounded-2xl p-5 shadow-sm">
                                        <h4 className="text-sm font-bold text-emerald-800 mb-3 flex items-center gap-2">
                                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path></svg>
                                            Sizin İçin Öngörüsü
                                        </h4>
                                        {selectedEvent.interpretation ? (
                                            <div className="text-sm text-gray-700 leading-relaxed font-medium" dangerouslySetInnerHTML={{ __html: selectedEvent.interpretation?.replace(/\n/g, '<br/>') }} />
                                        ) : (
                                            <div className="text-sm text-gray-500 italic">Yorum yüklenemedi veya boş döndü. Lütfen hatayı bildirin.</div>
                                        )}
                                    </div>
                                    <button
                                        onClick={handleReportError}
                                        disabled={reportingError || hasReported}
                                        className={`w-full text-xs font-bold py-3 rounded-xl transition-colors border ${hasReported ? 'bg-gray-100 text-gray-400 border-gray-200' : 'bg-red-50 text-red-600 border-red-100 hover:bg-red-100'}`}
                                    >
                                        {reportingError ? "Bildiriliyor..." : hasReported ? "İncelenmek Üzere Bildirildi" : "Hata Bildir / Veri Boş"}
                                    </button>
                                </div>
                            ) : (
                                <div className="bg-gray-50 border border-gray-100 border-dashed rounded-2xl p-6 text-center">
                                    <div className="w-12 h-12 bg-[#3DA7B5]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <svg className="w-6 h-6 text-[#3DA7B5]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path></svg>
                                    </div>
                                    <p className="text-gray-600 text-sm font-medium leading-relaxed mb-6">
                                        Bu gökyüzü olayının kişisel doğum haritanıza olan spesifik etkilerini astrolojik yapay zekamız ile analiz etmek için kilidini açın.
                                    </p>
                                    <button
                                        onClick={handleUnlock}
                                        disabled={interpreting}
                                        className="w-full relative bg-gradient-to-r from-[#3DA7B5] to-[#2a8a96] hover:from-[#2a8a96] hover:to-[#22727d] text-white font-bold py-3.5 px-6 rounded-xl shadow-lg shadow-[#3DA7B5]/30 transform hover:-translate-y-0.5 transition-all outline-none"
                                    >
                                        {interpreting ? (
                                            <span className="flex items-center justify-center gap-2">
                                                <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                                                Yorumlanıyor...
                                            </span>
                                        ) : (
                                            <span className="flex items-center justify-center gap-2">
                                                10 Token ile Kilidi Aç
                                            </span>
                                        )}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SkyEvents;
